import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Box } from '@mui/material';
import { Grid, Typography } from '@ts/storybook';
import KPI from '@components/KPI';
import { ListChart } from '@interfaces/App.interface';
import { generateDateArray, getDistinct, sortByAttribute } from '../../0_pages/DataBase/utils';
import { fetchData } from '@redux/slices/functionals/request/requestActions';
import { useAppDispatch } from '@redux/store';
import { WithTranslation, withTranslation } from 'react-i18next';

interface XTagElementProps extends WithTranslation {}

const GTXtagElement = (props: XTagElementProps) => {
  const { i18n } = props;
  const lng = i18n.language;

  const dispatch = useAppDispatch();
  const [slot, setSlot] = useState<{ id: number | undefined; value: string }>({ id: undefined, value: 'week' });

  const [renderedAreaCharts, setRenderedAreaCharts] = useState<ListChart[]>([
    {
      id: 1,
      title: 'Evolution',
      slot: 'week',
      data: undefined,
      getData: (slot: string) => getDataForChartReadingsTime(slot),
      loading: true,
    },
  ]);

  //#region Handlers
  const onChangeSlot = (item: any, type: string) => {
    setRenderedAreaCharts(prevItems => {
      return prevItems.map(area => {
        if (area.id === item.id) {
          return { ...area, slot: type, getData: () => area.getData(type) };
        }
        return area;
      });
    });
    setSlot({ id: item.id, value: type });
  };

  //#endregion

  //#region Functions
  const getDataForChartReadingsTime = async (type: string) => {
    let listReadings: any[] = [];
    const rangeDates = generateDateArray(new Date(), type);

    let reqListDates: any[] = [];

    for (let index = 0; index < rangeDates.length; index++) {
      const startDate = rangeDates[index];
      const endDate = rangeDates[index + 1] ?? new Date();

      const dayOfWeek = startDate.toLocaleDateString(lng, { weekday: 'long' });
      const month = startDate.toLocaleDateString(lng, { month: 'long' });

      listReadings.push({
        label: type === 'week' ? dayOfWeek : month,
        series: [],
      });
      reqListDates.push({
        url: '/api/v2/readings',
        data: {
          where: {
            timestamp: {
              $gte: startDate.toISOString(),
              $lte: endDate.toISOString(),
            },
          },
          findAndCount: true,
          limit: 0,
          offset: 0,
        },
      });
    }

    let results = await Promise.all(
      reqListDates.map(needs =>
        dispatch(
          fetchData({
            key: 'readings',
            url: needs.url,
            dataBody: needs.data,
            withoutCancel: true,
          })
        )
      )
    );
    results = results.map((res: any) => res.payload?.data);
    const values = [...results];

    const groupReadingCounts = (listEventCount: any[]): any[] => {
      return listEventCount.reduce((result, obj) => {
        const existingObj = result.find((item: any) => item.name === obj.name);

        if (existingObj) {
          existingObj.data.push(obj.count);
        } else {
          result.push({
            name: obj.name,
            count: obj.count,
          });
        }

        return result;
      }, []);
    };

    values.forEach((item: any, i: number) => {
      // return item no {count, rows}
      if (item) {
        let listReadingCount = getDistinct(item, ['readingTypeId']);
        listReadingCount = sortByAttribute(listReadingCount, 'name');
        listReadings[i].series = groupReadingCounts(listReadingCount);
      }
    });

    const disctinctEventTypes: any[] = [];

    listReadings.forEach(event => {
      event.series.forEach((type: any) => {
        if (!disctinctEventTypes.includes(type.name)) {
          disctinctEventTypes.push(type.name);
        }
      });
    });

    const orderSeries = sortByAttribute(
      disctinctEventTypes.map(type => {
        return {
          name: type,
          data: listReadings.map(l => {
            const serie = l.series.find((e: any) => e.name === type);
            return serie ? serie.count : 0;
          }),
        };
      }),
      'name',
      true
    );

    return {
      xaxis: {
        categories: listReadings.map(l => l.label),
      },
      series: orderSeries.length === 0 ? generateArrayEmpty(type) : orderSeries,
    };
  };
  const updateData = async (list: any[], saveFunction: any, idUpdate?: number) => {
    const updatedCards = await Promise.all(
      list.map(async chart => {
        if (idUpdate === undefined || idUpdate === chart.id) {
          const data = await chart.getData(slot.value);

          return { ...chart, data, loading: false };
        }
        return chart;
      })
    );
    saveFunction(updatedCards);
  };
  //#endregion

  //#region Render

  //#endregion

  useEffect(() => {
    updateData(renderedAreaCharts, setRenderedAreaCharts, slot.id);
  }, [slot]);

  return (
    <Box p={2} pr={0} pl={0} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid size={{ xs: 12 }} sx={{ paddingTop: '0!important', position: 'sticky', top: '-11px', backgroundColor: 'white' }}>
          <Typography variant="h6" color="primary" text="Temperature" />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <KPI.KPICard title="Max" withoutLabelDifference dataCard={{ value: 40, oldValue: 38 }} />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <KPI.KPICard title="Min" withoutLabelDifference dataCard={{ value: 20, oldValue: 18 }} />
        </Grid>
        <Grid size={{ xs: 4 }}>
          <KPI.KPICard title="Avg" withoutLabelDifference dataCard={{ value: 30, oldValue: 32 }} />
        </Grid>
      </Grid>
      <Box sx={{ flexGrow: 1, pt: 2, pb: 2 }}>
        <KPI.AreaChart
          {...props}
          id={renderedAreaCharts[0].id}
          title={renderedAreaCharts[0].title}
          loading={renderedAreaCharts[0].loading}
          dataChart={renderedAreaCharts[0].data}
          onChangeSlot={v => onChangeSlot(renderedAreaCharts[0], v)}
        />
      </Box>
    </Box>
  );
};

export default withTranslation()(GTXtagElement);

const generateArrayEmpty = (value: string): any => {
  if (value === 'week') {
    return [{ name: 'No data', data: Array(7).fill(0) }];
  } else if (value === 'month') {
    return [{ name: 'No data', data: Array(12).fill(0) }];
  } else {
    throw new Error('Invalid value. Must be either "week" or "month".');
  }
};
